import * as XLSX from 'xlsx'
import { CompanyType } from '@common/config/api/client'
import { CompanyFormValues } from './CreateOrUpdateCompanyDialog'

// Valid company types
const validCompanyTypes: CompanyType[] = ['SITE', 'RADIOLOGY_CENTER', 'SPONSOR', 'INTERNAL', 'INDEPENDENT_READER']

// Validate and normalize company type
const normalizeCompanyType = (type: string): CompanyType => {
  const normalizedType = type.trim().toUpperCase() as CompanyType
  if (validCompanyTypes.includes(normalizedType)) {
    return normalizedType
  }
  // Default to SITE if invalid type
  return 'SITE'
}

export const parseCSV = (text: string): CompanyFormValues[] => {
  const lines = text.trim().split('\n')
  if (lines.length <= 1) return [] // Return empty array if only headers or empty file

  const headers = lines[0].split(';').map((h) => h.trim())

  return lines
    .slice(1)
    .map((line) => {
      const values = line.split(';').map((v) => v.trim())
      const row: Record<string, string> = {}

      headers.forEach((key, i) => {
        row[key.toLowerCase()] = values[i]
      })

      return {
        name: row.name || '',
        type: normalizeCompanyType(row.type || 'SITE'),
        contact_person_name: row.contact_person_name || null,
        email: row.email || null,
        phone: row.phone?.toString() || null,
      }
    })
    .filter((company) => company.name) // Filter out companies without a name
}

export const parseExcel = async (file: File): Promise<CompanyFormValues[]> => {
  try {
    const data = await file.arrayBuffer()
    const workbook = XLSX.read(data, { type: 'array' })
    const sheetName = workbook.SheetNames[0]
    const worksheet = workbook.Sheets[sheetName]
    const jsonData = XLSX.utils.sheet_to_json(worksheet) as Record<string, string>[]

    return jsonData.map((row) => transformRowFromExcel(row)).filter((company) => company.name) // Filter out companies without a name
  } catch (error) {
    console.error('Error parsing Excel file:', error)
    return []
  }
}

const normalizeKey = (key: string) => key.trim().toLowerCase().replace(/\s+/g, '_')

const transformRowFromExcel = (row: Record<string, string>): CompanyFormValues => {
  const normalized: Record<string, string> = {}
  for (const key in row) {
    normalized[normalizeKey(key)] = row[key]
  }

  return {
    name: normalized.name || '',
    type: normalizeCompanyType(normalized.type || 'SITE'),
    contact_person_name: normalized.contact_person_name || null,
    email: normalized.email || null,
    phone: normalized.phone?.toString() || null,
  }
}
